import type { FormInstance, FormTemplate, FormType } from '@/services/api/forms';

/**
 * Filter state shared by the Form Library filter bar (search box, Form Type
 * dropdown, Status dropdown).
 */
export interface FormLibraryFilterState {
  filterFormType: FormType | 'all';
  filterStatus: string;
  searchTerm: string;
}

/**
 * Predicate for the "My Forms" (instances) list.
 *
 * TRUS-278: the Form Type dropdown is rendered for the instances view as well
 * as the templates view, but the instances predicate previously ignored it, so
 * selecting a type silently did nothing. Applying the same case-insensitive,
 * key-vs-key comparison the templates list uses keeps the two views consistent.
 */
export function matchesInstanceFilters(
  instance: FormInstance,
  { filterFormType, filterStatus, searchTerm }: FormLibraryFilterState,
): boolean {
  if (
    filterFormType !== 'all' &&
    instance.form_type?.toLowerCase() !== filterFormType.toLowerCase()
  ) {
    return false;
  }
  if (filterStatus !== 'all' && instance.status !== filterStatus) {
    return false;
  }
  if (
    searchTerm &&
    !instance.form_template_name.toLowerCase().includes(searchTerm.toLowerCase())
  ) {
    return false;
  }
  return true;
}

/**
 * Predicate for the "Available Templates" list — decides whether a template
 * should surface in the Form Library.
 *
 * The Form Library is the "Forms & consents" page under **Work**, and the Forms
 * tab of a member profile. Both are member-facing surfaces, so both show only
 * member-scoped templates.
 *
 * TRUS-422 first applied this to the member-profile context only, leaving the
 * Work page showing everything. That put Somos' promotora attendance forms
 * (target_entity 'registered_user') and their anonymous outreach log
 * ('activity_event') in a list titled "Forms & consents", where neither can be
 * filled: an activity_event form has no member key, and a registered_user form
 * is filled from its assignee's My Forms inbox. Those belong to the Activity
 * Tracking section — Outreach Activities, Hub Meetings & Trainings, My Forms —
 * which is where staff actually reach them.
 *
 * target_entity is optional and defaults to 'member', so a missing/legacy value
 * is treated as member-scoped.
 *
 * NOTE: this hides 'registered_user' templates from the Form Library, which is
 * where the Assign-to-staff button used to live. That action moved to the Form
 * Builder (FormTemplateBuilder), which is the admin surface for those templates.
 */
export function isTemplateVisibleForContext(
  template: Pick<FormTemplate, 'target_entity'>,
): boolean {
  return (template.target_entity ?? 'member') === 'member';
}
